import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  CheckCircle2,
  User,
  Globe,
  MapPin,
  Phone,
  CreditCard,
  Plane,
  Users,
  History,
  Contact,
  Briefcase,
  GraduationCap,
  Shield,
  Save,
  HelpCircle,
  AlertTriangle,
  Check,
  X,
  MessageCircle,
  Sparkles,
  Download,
  Upload,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Info,
  Lightbulb,
  FileCheck,
  XCircle,
  Loader2,
  Scan,
  Camera
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import DashboardLayout from "../components/DashboardLayout";
import PassportScanner from "../components/PassportScanner";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Step Configuration
const STEPS = [
  { id: 1, name: "Información Personal", icon: User, description: "Nombre, fecha de nacimiento, género" },
  { id: 2, name: "Nacionalidad", icon: Globe, description: "País de origen y residencia" },
  { id: 3, name: "Dirección y Contacto", icon: MapPin, description: "Domicilio actual y teléfonos" },
  { id: 4, name: "Pasaporte", icon: CreditCard, description: "Datos del documento de viaje" },
  { id: 5, name: "Viaje", icon: Plane, description: "Propósito y fechas del viaje" },
  { id: 6, name: "Viajes Previos", icon: History, description: "Historial de viajes a EE.UU." },
  { id: 7, name: "Contacto en EE.UU.", icon: Contact, description: "Persona o empresa de contacto" },
  { id: 8, name: "Familia", icon: Users, description: "Información de padres y cónyuge" },
  { id: 9, name: "Trabajo", icon: Briefcase, description: "Empleo actual e ingresos" },
  { id: 10, name: "Educación", icon: GraduationCap, description: "Formación académica" },
  { id: 11, name: "Seguridad", icon: Shield, description: "Preguntas de seguridad" },
  { id: 12, name: "Confirmación", icon: CheckCircle2, description: "Revisar y confirmar" },
];

// Required fields per step for progress calculation
const STEP_FIELDS = {
  1: ["surname", "given_name", "sex", "marital_status", "birth_date", "birth_city", "birth_country"],
  2: ["nationality", "national_id"],
  3: ["home_address", "home_city", "home_state", "home_country", "phone_primary", "email"],
  4: ["passport_type", "passport_number", "passport_country", "passport_city_issued", "passport_issue_date", "passport_expiry_date"],
  5: ["travel_purpose", "who_pays_trip"],
  6: ["has_previous_us_travel", "has_previous_visa"],
  7: ["us_contact_name", "us_contact_address", "us_contact_city", "us_contact_state"],
  8: ["father_surname", "father_given_name", "mother_surname", "mother_given_name"],
  9: ["occupation"],
  10: ["education_level"],
  11: ["security_disease", "security_disorder", "security_drug_addict", "security_arrested", "security_controlled_substances", "security_prostitution", "security_money_laundering", "security_trafficking", "security_terrorist", "security_genocide"],
  12: ["final_confirmation"]
};

// Calculate progress for a step
const calculateStepProgress = (stepId, formData) => {
  const fields = STEP_FIELDS[stepId] || [];
  if (fields.length === 0) return { filled: 0, total: 0 };
  
  const filled = fields.filter(field => {
    const value = formData[field];
    if (typeof value === 'boolean') return true; // Booleans are always "answered"
    if (typeof value === 'string') return value.trim() !== '';
    return value !== undefined && value !== null;
  }).length;
  
  return { filled, total: fields.length };
};

// Calculate total form progress
const calculateTotalProgress = (formData) => {
  let totalFilled = 0;
  let totalFields = 0;
  
  Object.keys(STEP_FIELDS).forEach(stepId => {
    const progress = calculateStepProgress(parseInt(stepId), formData);
    totalFilled += progress.filled;
    totalFields += progress.total;
  });
  
  return {
    filled: totalFilled,
    total: totalFields,
    percentage: totalFields > 0 ? Math.round((totalFilled / totalFields) * 100) : 0
  };
};

// Risk Detection Configuration
const RISK_RULES = [
  {
    id: "visa_refused",
    field: "visa_was_refused",
    condition: (value) => value === true,
    level: "high",
    title: "Visa Negada Anteriormente",
    message: "Has indicado que te negaron una visa. Esto requiere documentación adicional y preparación especial para la entrevista.",
    documents: ["Carta explicativa de la negación", "Documentos de soporte adicionales"]
  },
  {
    id: "visa_cancelled",
    field: "visa_was_cancelled",
    condition: (value) => value === true,
    level: "high",
    title: "Visa Cancelada/Revocada",
    message: "Una visa cancelada es un factor de riesgo importante. Prepara documentación que demuestre cambio de circunstancias.",
    documents: ["Carta explicativa", "Pruebas de cambio de situación"]
  },
  {
    id: "passport_expiring",
    field: "passport_expiry_date",
    condition: (value) => {
      if (!value) return false;
      const expiry = new Date(value);
      const sixMonths = new Date();
      sixMonths.setMonth(sixMonths.getMonth() + 6);
      return expiry < sixMonths;
    },
    level: "medium",
    title: "Pasaporte Próximo a Vencer",
    message: "Tu pasaporte vence en menos de 6 meses. Se recomienda renovarlo antes de la cita consular.",
    documents: ["Nuevo pasaporte o constancia de renovación en trámite"]
  },
  {
    id: "lost_passport",
    field: "has_lost_passport",
    condition: (value) => value === true,
    level: "medium",
    title: "Pasaporte Perdido/Robado",
    message: "Deberás explicar las circunstancias de la pérdida del pasaporte anterior.",
    documents: ["Denuncia policial (si aplica)", "Declaración escrita"]
  },
  {
    id: "security_issues",
    fields: ["security_arrested", "security_controlled_substances", "security_terrorist"],
    condition: (formData) => {
      return formData.security_arrested === true || 
             formData.security_controlled_substances === true ||
             formData.security_terrorist === true;
    },
    level: "critical",
    title: "Antecedentes de Seguridad",
    message: "Has indicado antecedentes que requieren atención especial. Consulta con tu asesor antes de continuar.",
    documents: ["Documentación legal completa", "Carta de abogado (recomendado)"]
  },
  {
    id: "self_employed_no_docs",
    field: "occupation",
    condition: (value, formData) => {
      return (value === "self_employed" || value === "business_owner") && !formData.employer_name;
    },
    level: "low",
    title: "Negocio Propio - Documentación",
    message: "Como trabajador independiente, necesitarás documentación adicional de tu negocio.",
    documents: ["Estados financieros", "Registro fiscal (RFC/RUC)", "Acta constitutiva"]
  }
];

// Dynamic Document Checklist based on form data
const getRequiredDocuments = (formData) => {
  const baseDocuments = [
    { id: "passport", name: "Pasaporte vigente", required: true, category: "Identificación" },
    { id: "photo", name: "Fotografía digital DS-160", required: true, category: "Identificación" },
    { id: "ds160_confirmation", name: "Confirmación DS-160", required: true, category: "Formularios" },
  ];

  const conditionalDocuments = [];

  // Employment-based documents
  if (formData.occupation === "employed") {
    conditionalDocuments.push(
      { id: "employment_letter", name: "Carta laboral", required: true, category: "Empleo" },
      { id: "pay_stubs", name: "Recibos de nómina (3 meses)", required: true, category: "Empleo" }
    );
  }

  if (formData.occupation === "self_employed" || formData.occupation === "business_owner") {
    conditionalDocuments.push(
      { id: "business_registration", name: "Registro de empresa / RFC", required: true, category: "Negocio" },
      { id: "financial_statements", name: "Estados financieros", required: true, category: "Negocio" },
      { id: "tax_returns", name: "Declaraciones de impuestos", required: true, category: "Negocio" }
    );
  }

  // Travel purpose documents
  if (formData.travel_purpose === "business") {
    conditionalDocuments.push(
      { id: "invitation_letter", name: "Carta de invitación de empresa en EE.UU.", required: true, category: "Viaje" },
      { id: "business_itinerary", name: "Itinerario de negocios", required: false, category: "Viaje" }
    );
  }

  if (formData.travel_purpose === "medical") {
    conditionalDocuments.push(
      { id: "medical_diagnosis", name: "Diagnóstico médico", required: true, category: "Médico" },
      { id: "doctor_letter", name: "Carta del médico en EE.UU.", required: true, category: "Médico" },
      { id: "treatment_cost", name: "Estimación de costos de tratamiento", required: true, category: "Médico" }
    );
  }

  // Family/marital documents
  if (formData.marital_status === "married" || formData.marital_status === "common_law") {
    conditionalDocuments.push(
      { id: "marriage_certificate", name: "Acta de matrimonio", required: true, category: "Familia" }
    );
  }

  // Previous visa issues
  if (formData.visa_was_refused) {
    conditionalDocuments.push(
      { id: "refusal_explanation", name: "Carta explicativa de negación", required: true, category: "Historial" },
      { id: "changed_circumstances", name: "Prueba de cambio de circunstancias", required: true, category: "Historial" }
    );
  }

  // Property ownership (strong ties)
  conditionalDocuments.push(
    { id: "property_deed", name: "Escrituras de propiedad", required: false, category: "Vínculos" },
    { id: "bank_statements", name: "Estados de cuenta bancarios (3 meses)", required: true, category: "Financiero" }
  );

  // Education
  if (formData.education_level && formData.education_level !== "none") {
    conditionalDocuments.push(
      { id: "education_certificate", name: "Título o constancia de estudios", required: false, category: "Educación" }
    );
  }

  return [...baseDocuments, ...conditionalDocuments];
};

// Field-specific AI help suggestions
const FIELD_HELP = {
  surname: {
    title: "Apellido(s)",
    tips: "Escribe exactamente como aparece en tu pasaporte, en MAYÚSCULAS.",
    commonIssues: ["Si tienes dos apellidos, incluye ambos", "No uses acentos si tu pasaporte no los tiene"]
  },
  visa_was_refused: {
    title: "Visa Negada",
    tips: "Sé honesto. Mentir es peor que una negación previa.",
    commonIssues: ["Las negaciones bajo 214(b) son comunes y superables", "Prepara documentación que demuestre vínculos fuertes con tu país"]
  },
  travel_purpose: {
    title: "Propósito del Viaje",
    tips: "Selecciona el propósito principal. Si tienes múltiples razones, elige la más importante.",
    commonIssues: ["Turismo incluye visitar familia", "Negocios requiere carta de invitación"]
  },
  occupation: {
    title: "Ocupación",
    tips: "Selecciona tu actividad principal actual.",
    commonIssues: ["Si tienes negocio propio, necesitarás más documentos", "Estudiantes deben mostrar constancia de inscripción"]
  }
};

// Country options (simplified)
const COUNTRIES = [
  "México", "Colombia", "Argentina", "Perú", "Chile", "Ecuador", "Venezuela", "Guatemala",
  "Honduras", "El Salvador", "Nicaragua", "Costa Rica", "Panamá", "República Dominicana",
  "Puerto Rico", "Cuba", "Brasil", "España", "Estados Unidos", "Canadá", "Otro"
];

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming", "District of Columbia"
];

// AI Help Modal Component
const AIHelpModal = ({ isOpen, onClose, currentStep }) => {
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAsk = async () => {
    if (!question.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        message: `[DS-160 Asistente - Paso ${currentStep}] ${question}`,
        session_id: "ds160_assistant"
      });
      setResponse(res.data.response);
    } catch (error) {
      toast.error("Error al consultar el asistente");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b border-navy-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-navy-900">Asistente DS-160</h3>
              <p className="text-xs text-navy-500">Pregunta cualquier duda</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-navy-50 rounded-lg">
            <X className="w-5 h-5 text-navy-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {response && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-sm text-navy-700 whitespace-pre-wrap">{response}</p>
            </div>
          )}
          
          <div className="space-y-2">
            <Textarea
              placeholder="Escribe tu pregunta sobre el formulario DS-160..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="min-h-[100px] resize-none"
            />
            <Button
              onClick={handleAsk}
              disabled={loading || !question.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Preguntar
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="p-4 border-t border-navy-100 bg-navy-50">
          <p className="text-xs text-navy-500 text-center">
            El asistente te ayuda con dudas del formulario. Para asesoría legal, contacta a tu asesor.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Overall Progress Summary Component
const ProgressSummary = ({ savedData, onStepClick, saving, hasUnsavedChanges }) => {
  const totalProgress = calculateTotalProgress(savedData);
  const stepsComplete = STEPS.filter(step => {
    const progress = calculateStepProgress(step.id, savedData);
    return progress.total > 0 && progress.filled === progress.total;
  }).length;

  // Estimate time remaining (approximately 2 min per incomplete step)
  const incompleteSteps = STEPS.length - stepsComplete;
  const estimatedMinutes = incompleteSteps * 2;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-navy-800 to-navy-900 rounded-2xl p-6 text-white mb-6"
      data-testid="progress-summary"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold">Progreso del Formulario</h3>
          <p className="text-navy-300 text-sm flex items-center gap-2">
            DS-160 - Solicitud de Visa
            {saving && (
              <span className="flex items-center gap-1 text-amber-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                Guardando...
              </span>
            )}
            {!saving && hasUnsavedChanges && (
              <span className="text-amber-400 text-xs">• Cambios sin guardar</span>
            )}
            {!saving && !hasUnsavedChanges && totalProgress.filled > 0 && (
              <span className="text-emerald-400 text-xs flex items-center gap-1">
                <Check className="w-3 h-3" />
                Guardado
              </span>
            )}
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold">{totalProgress.percentage}%</div>
          <p className="text-navy-300 text-sm">{totalProgress.filled}/{totalProgress.total} campos</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-3 bg-navy-700 rounded-full overflow-hidden mb-4">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${totalProgress.percentage}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={`h-full rounded-full ${
            totalProgress.percentage === 100 
              ? "bg-emerald-500" 
              : totalProgress.percentage >= 75 
              ? "bg-emerald-400" 
              : totalProgress.percentage >= 50 
              ? "bg-amber-400" 
              : "bg-amber-500"
          }`}
        />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="bg-navy-700/50 rounded-xl p-3">
          <div className="text-2xl font-bold text-emerald-400">{stepsComplete}</div>
          <p className="text-xs text-navy-300">Pasos completos</p>
        </div>
        <div className="bg-navy-700/50 rounded-xl p-3">
          <div className="text-2xl font-bold text-amber-400">{incompleteSteps}</div>
          <p className="text-xs text-navy-300">Pasos pendientes</p>
        </div>
        <div className="bg-navy-700/50 rounded-xl p-3">
          <div className="flex items-center justify-center gap-1">
            <Clock className="w-5 h-5 text-blue-400" />
            <span className="text-2xl font-bold text-blue-400">~{estimatedMinutes}</span>
          </div>
          <p className="text-xs text-navy-300">min restantes</p>
        </div>
      </div>

      {/* Quick Jump to Incomplete Steps */}
      {incompleteSteps > 0 && incompleteSteps <= 3 && (
        <div className="mt-4 pt-4 border-t border-navy-700">
          <p className="text-xs text-navy-400 mb-2">Pasos pendientes:</p>
          <div className="flex flex-wrap gap-2">
            {STEPS.filter(step => {
              const progress = calculateStepProgress(step.id, savedData);
              return progress.total > 0 && progress.filled < progress.total;
            }).slice(0, 3).map(step => (
              <button
                key={step.id}
                onClick={() => onStepClick(step.id)}
                className="px-3 py-1 bg-navy-700 hover:bg-navy-600 rounded-lg text-xs transition-colors"
              >
                {step.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Risk Detection Component
const RiskAlerts = ({ formData, risks }) => {
  const [expanded, setExpanded] = useState(true);

  // Calculate active risks
  const activeRisks = RISK_RULES.filter(rule => {
    if (rule.fields) {
      return rule.condition(formData);
    }
    const value = formData[rule.field];
    return rule.condition(value, formData);
  });

  if (activeRisks.length === 0) return null;

  const criticalCount = activeRisks.filter(r => r.level === "critical").length;
  const highCount = activeRisks.filter(r => r.level === "high").length;
  const mediumCount = activeRisks.filter(r => r.level === "medium").length;

  const overallLevel = criticalCount > 0 ? "critical" : highCount > 0 ? "high" : mediumCount > 0 ? "medium" : "low";

  const levelColors = {
    critical: "bg-red-500",
    high: "bg-orange-500",
    medium: "bg-amber-500",
    low: "bg-blue-500"
  };

  const levelBorders = {
    critical: "border-red-200 bg-red-50",
    high: "border-orange-200 bg-orange-50",
    medium: "border-amber-200 bg-amber-50",
    low: "border-blue-200 bg-blue-50"
  };

  const levelText = {
    critical: "Riesgo Crítico",
    high: "Riesgo Alto",
    medium: "Riesgo Medio",
    low: "Riesgo Bajo"
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`rounded-2xl border-2 ${levelBorders[overallLevel]} mb-6 overflow-hidden`}
      data-testid="risk-alerts"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${levelColors[overallLevel]} flex items-center justify-center`}>
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-navy-900">{levelText[overallLevel]}</h3>
            <p className="text-sm text-navy-600">{activeRisks.length} alerta{activeRisks.length > 1 ? 's' : ''} detectada{activeRisks.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {criticalCount > 0 && <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{criticalCount}</span>}
            {highCount > 0 && <span className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">{highCount}</span>}
            {mediumCount > 0 && <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">{mediumCount}</span>}
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 text-navy-400" /> : <ChevronDown className="w-5 h-5 text-navy-400" />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-navy-200"
          >
            <div className="p-4 space-y-3">
              {activeRisks.map((risk, index) => (
                <div 
                  key={risk.id}
                  className={`p-4 rounded-xl border ${
                    risk.level === "critical" ? "border-red-300 bg-red-50" :
                    risk.level === "high" ? "border-orange-300 bg-orange-50" :
                    risk.level === "medium" ? "border-amber-300 bg-amber-50" :
                    "border-blue-300 bg-blue-50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      risk.level === "critical" ? "bg-red-500" :
                      risk.level === "high" ? "bg-orange-500" :
                      risk.level === "medium" ? "bg-amber-500" :
                      "bg-blue-500"
                    }`}>
                      <AlertCircle className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-navy-900">{risk.title}</h4>
                      <p className="text-sm text-navy-600 mt-1">{risk.message}</p>
                      {risk.documents && risk.documents.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-navy-700 mb-1">Documentos recomendados:</p>
                          <div className="flex flex-wrap gap-1">
                            {risk.documents.map((doc, i) => (
                              <span key={i} className="px-2 py-0.5 bg-white rounded text-xs text-navy-600 border">
                                {doc}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Field Help Tooltip Component
const FieldHelpButton = ({ fieldId, onAskAI }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const fieldHelp = FIELD_HELP[fieldId];

  if (!fieldHelp) return null;

  return (
    <div className="relative inline-block ml-2">
      <button
        type="button"
        onClick={() => setShowTooltip(!showTooltip)}
        className="w-5 h-5 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 flex items-center justify-center transition-colors"
        data-testid={`field-help-${fieldId}`}
      >
        <HelpCircle className="w-3 h-3" />
      </button>

      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute z-50 left-0 top-7 w-72 bg-white rounded-xl shadow-xl border border-navy-100 p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                <h4 className="font-semibold text-navy-900 text-sm">{fieldHelp.title}</h4>
              </div>
              <button onClick={() => setShowTooltip(false)} className="text-navy-400 hover:text-navy-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-navy-600 mb-3">{fieldHelp.tips}</p>
            {fieldHelp.commonIssues && (
              <div className="mb-3">
                <p className="text-xs font-medium text-navy-500 mb-1">Errores comunes:</p>
                <ul className="text-xs text-navy-600 space-y-1">
                  {fieldHelp.commonIssues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-amber-500">•</span>
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={() => {
                onAskAI(`¿Cómo debo completar el campo "${fieldHelp.title}"?`);
                setShowTooltip(false);
              }}
              className="w-full py-2 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors"
            >
              <Sparkles className="w-3 h-3" />
              Preguntar a la IA
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Document Checklist Component
const DocumentChecklist = ({ formData, uploadedDocs = [], onUpload }) => {
  const [expanded, setExpanded] = useState(false);
  const requiredDocs = getRequiredDocuments(formData);

  // Group documents by category
  const groupedDocs = requiredDocs.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {});

  const totalRequired = requiredDocs.filter(d => d.required).length;
  const uploadedRequired = requiredDocs.filter(d => d.required && uploadedDocs.includes(d.id)).length;
  const progress = totalRequired > 0 ? Math.round((uploadedRequired / totalRequired) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-navy-100 shadow-sm overflow-hidden mb-6"
      data-testid="document-checklist"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-navy-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-navy-900">Documentos Requeridos</h3>
            <p className="text-sm text-navy-500">{uploadedRequired}/{totalRequired} obligatorios subidos</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-24 h-2 bg-navy-100 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${progress === 100 ? "bg-emerald-500" : "bg-blue-500"}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-sm font-medium text-navy-600">{progress}%</span>
          {expanded ? <ChevronUp className="w-5 h-5 text-navy-400" /> : <ChevronDown className="w-5 h-5 text-navy-400" />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-navy-100"
          >
            <div className="p-4 space-y-4">
              {Object.entries(groupedDocs).map(([category, docs]) => (
                <div key={category}>
                  <h4 className="text-sm font-semibold text-navy-700 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    {category}
                  </h4>
                  <div className="space-y-2 ml-4">
                    {docs.map(doc => {
                      const isUploaded = uploadedDocs.includes(doc.id);
                      return (
                        <div 
                          key={doc.id}
                          className={`flex items-center justify-between p-3 rounded-xl border ${
                            isUploaded 
                              ? "bg-emerald-50 border-emerald-200" 
                              : doc.required 
                              ? "bg-amber-50 border-amber-200" 
                              : "bg-navy-50 border-navy-200"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {isUploaded ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            ) : doc.required ? (
                              <AlertCircle className="w-5 h-5 text-amber-500" />
                            ) : (
                              <FileText className="w-5 h-5 text-navy-400" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-navy-900">{doc.name}</p>
                              <p className="text-xs text-navy-500">
                                {doc.required ? "Obligatorio" : "Opcional"}
                              </p>
                            </div>
                          </div>
                          {!isUploaded && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onUpload(doc.id)}
                              className="text-xs"
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Subir
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="pt-4 border-t border-navy-100">
                <p className="text-xs text-navy-500 flex items-start gap-2">
                  <Info className="w-4 h-4 shrink-0" />
                  Esta lista se actualiza automáticamente según tus respuestas. Los documentos opcionales aumentan tu probabilidad de aprobación.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Contextual AI Field Assistant
const ContextualAIAssistant = ({ fieldId, fieldLabel, isOpen, onClose, currentValue }) => {
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);

  const askAI = async (specificQuestion) => {
    setLoading(true);
    try {
      const context = currentValue ? `El usuario ha escrito: "${currentValue}"` : "El campo está vacío";
      const res = await axios.post(`${API}/chat`, {
        message: `[DS-160 Campo: ${fieldLabel}] ${context}. Pregunta: ${specificQuestion}`,
        session_id: "ds160_field_assistant"
      });
      setResponse(res.data.response);
    } catch (error) {
      toast.error("Error al consultar el asistente");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && !response) {
      askAI(`¿Cómo debo llenar correctamente el campo "${fieldLabel}"? Dame consejos específicos y errores comunes a evitar.`);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="mt-2 p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl border border-emerald-200"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-medium text-emerald-800">Asistente IA</span>
        </div>
        <button onClick={onClose} className="text-navy-400 hover:text-navy-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
        </div>
      ) : (
        <p className="text-sm text-navy-700 whitespace-pre-wrap">{response}</p>
      )}
    </motion.div>
  );
};

// Oficial Comments Alert Component
const OficialCommentsAlert = ({ comments, pendingCount, onViewComment }) => {
  const [expanded, setExpanded] = useState(true);
  
  const pendingComments = comments.filter(c => c.requires_correction && !c.resolved);
  
  if (pendingComments.length === 0) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 mb-6"
      data-testid="oficial-comments-alert"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-amber-900">Correcciones Requeridas</h3>
            <p className="text-sm text-amber-700">{pendingCount} campo(s) necesitan corrección</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-amber-500" /> : <ChevronDown className="w-5 h-5 text-amber-500" />}
      </button>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-4 space-y-2"
          >
            {pendingComments.map((comment, index) => {
              const fieldLabel = FIELD_HELP[comment.field_id]?.title || comment.field_id;
              return (
                <div 
                  key={index}
                  className="p-3 bg-white rounded-xl border border-amber-200 cursor-pointer hover:bg-amber-50 transition-colors"
                  onClick={() => onViewComment(comment.field_id)}
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-navy-900 text-sm">{fieldLabel}</p>
                      <p className="text-sm text-navy-600 mt-1">{comment.comment}</p>
                      <p className="text-xs text-navy-400 mt-1">
                        Por: {comment.oficial_name} • {new Date(comment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Field Comment Indicator
const FieldCommentIndicator = ({ comment, onResolve }) => {
  if (!comment || comment.resolved) return null;
  
  return (
    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
      <div className="flex items-start gap-2">
        <MessageCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-navy-700">{comment.comment}</p>
          <p className="text-xs text-navy-500 mt-1">
            Comentario de: {comment.oficial_name}
          </p>
        </div>
        <button
          onClick={onResolve}
          className="px-2 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs rounded-lg transition-colors"
        >
          Marcar resuelto
        </button>
      </div>
    </div>
  );
};

// Step Progress Component
const StepProgress = ({ currentStep, onStepClick, completedSteps, savedData }) => {
  return (
    <div className="hidden lg:block w-64 shrink-0">
      <div className="sticky top-4 space-y-2">
        {STEPS.map((step) => {
          const Icon = step.icon;
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = currentStep === step.id;
          const progress = calculateStepProgress(step.id, savedData);
          const isStepComplete = progress.total > 0 && progress.filled === progress.total;
          
          return (
            <button
              key={step.id}
              onClick={() => onStepClick(step.id)}
              data-testid={`ds160-step-${step.id}`}
              className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                isCurrent
                  ? "bg-emerald-500 text-white shadow-lg"
                  : isStepComplete
                  ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  : "bg-white text-navy-600 hover:bg-navy-50 border border-navy-100"
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isCurrent ? "bg-white/20" : isStepComplete ? "bg-emerald-200" : "bg-navy-100"
              }`}>
                {isStepComplete && !isCurrent ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isCurrent ? "text-white" : ""}`}>
                  {step.name}
                </p>
                {/* Progress counter */}
                {progress.total > 0 && (
                  <p className={`text-xs ${isCurrent ? "text-white/70" : isStepComplete ? "text-emerald-600" : "text-navy-400"}`}>
                    {progress.filled}/{progress.total} respondidas
                  </p>
                )}
              </div>
              {/* Visual progress indicator */}
              {progress.total > 0 && (
                <div className={`w-10 h-10 relative ${isCurrent ? "" : ""}`}>
                  <svg className="w-10 h-10 transform -rotate-90">
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      fill="none"
                      stroke={isCurrent ? "rgba(255,255,255,0.2)" : "#e5e7eb"}
                      strokeWidth="3"
                    />
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      fill="none"
                      stroke={isCurrent ? "#fff" : isStepComplete ? "#10b981" : "#6b7280"}
                      strokeWidth="3"
                      strokeDasharray={`${(progress.filled / progress.total) * 100.53} 100.53`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${
                    isCurrent ? "text-white" : isStepComplete ? "text-emerald-600" : "text-navy-500"
                  }`}>
                    {Math.round((progress.filled / progress.total) * 100)}%
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Field Components
const FormField = ({ label, required, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-navy-700 font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-navy-500">{hint}</p>}
  </div>
);

const YesNoField = ({ label, value, onChange, hint }) => (
  <div className="flex items-center justify-between p-4 bg-navy-50 rounded-xl">
    <div>
      <p className="font-medium text-navy-900">{label}</p>
      {hint && <p className="text-xs text-navy-500 mt-1">{hint}</p>}
    </div>
    <div className="flex items-center gap-3">
      <span className={`text-sm ${!value ? "font-medium text-navy-900" : "text-navy-400"}`}>No</span>
      <Switch checked={value} onCheckedChange={onChange} />
      <span className={`text-sm ${value ? "font-medium text-navy-900" : "text-navy-400"}`}>Sí</span>
    </div>
  </div>
);

// Main Component
const DS160Assistant = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showHelp, setShowHelp] = useState(false);
  const [showPassportScanner, setShowPassportScanner] = useState(false);
  const [formData, setFormData] = useState({
    confirmation_number: "",
    completed: false,
    current_step: 1,
  });
  const [savedData, setSavedData] = useState({}); // Track what's actually saved in DB
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [oficialComments, setOficialComments] = useState([]); // Comments from oficial
  const [pendingCorrections, setPendingCorrections] = useState(0);
  const autoSaveTimerRef = useRef(null);

  // Handle passport OCR data
  const handlePassportData = (ds160Fields) => {
    setFormData(prev => ({
      ...prev,
      surname: ds160Fields.surnames || prev.surname,
      given_name: ds160Fields.given_names || prev.given_name,
      sex: ds160Fields.sex || prev.sex,
      birth_date: ds160Fields.date_of_birth || prev.birth_date,
      birth_city: ds160Fields.city_of_birth || prev.birth_city,
      birth_country: ds160Fields.country_of_birth || prev.birth_country,
      nationality: ds160Fields.nationality || prev.nationality,
      passport_number: ds160Fields.passport_number || prev.passport_number,
      passport_issue_date: ds160Fields.passport_issue_date || prev.passport_issue_date,
      passport_expiry_date: ds160Fields.passport_expiry_date || prev.passport_expiry_date,
      passport_country: ds160Fields.passport_issuing_country || prev.passport_country
    }));
    setShowPassportScanner(false);
  };

  useEffect(() => {
    fetchCase();
  }, []);

  // Auto-save with debounce when formData changes
  useEffect(() => {
    if (!caseData?.id || loading) return;
    
    // Check if there are actual changes from savedData
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(savedData);
    setHasUnsavedChanges(hasChanges);
    
    if (hasChanges) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      // Set new timer for auto-save (1.5 seconds after last change)
      autoSaveTimerRef.current = setTimeout(() => {
        autoSave();
      }, 1500);
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, caseData?.id, loading]);

  const autoSave = async () => {
    if (!caseData?.id) return;
    
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        current_step: currentStep
      };
      await axios.post(`${API}/cases/${caseData.id}/ds160`, dataToSave);
      setSavedData(dataToSave);
      setHasUnsavedChanges(false);
      // Silent save - no toast for auto-save to avoid spam
    } catch (error) {
      console.error("Auto-save failed:", error);
      // Don't show error toast for auto-save failures
    } finally {
      setSaving(false);
    }
  };

  const fetchCase = async () => {
    try {
      const res = await axios.get(`${API}/cases/my-case`);
      setCaseData(res.data);
      if (res.data?.ds160_data) {
        setFormData(res.data.ds160_data);
        setSavedData(res.data.ds160_data); // Track saved data
        setCurrentStep(res.data.ds160_data.current_step || 1);
        // Calculate completed steps
        const completed = [];
        for (let i = 1; i < (res.data.ds160_data.current_step || 1); i++) {
          completed.push(i);
        }
        setCompletedSteps(completed);
      }
      
      // Fetch oficial comments if case exists
      if (res.data?.id) {
        try {
          const commentsRes = await axios.get(`${API}/cases/${res.data.id}/ds160-comments`);
          setOficialComments(commentsRes.data.comments || []);
          setPendingCorrections(commentsRes.data.pending_count || 0);
        } catch (err) {
          console.log("No comments yet");
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar datos del caso");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (advance = false) => {
    if (!caseData?.id) return;
    
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        current_step: advance ? currentStep + 1 : currentStep
      };
      await axios.post(`${API}/cases/${caseData.id}/ds160`, dataToSave);
      
      if (advance) {
        if (currentStep < STEPS.length) {
          setCompletedSteps(prev => [...new Set([...prev, currentStep])]);
          setCurrentStep(currentStep + 1);
          setFormData(prev => ({ ...prev, current_step: currentStep + 1 }));
          toast.success("Progreso guardado");
        } else {
          toast.success("¡Formulario DS-160 completado!");
          navigate("/dashboard");
        }
      } else {
        toast.success("Cambios guardados");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleExportPDF = async () => {
    if (!caseData?.id) {
      toast.error("No hay caso para exportar");
      return;
    }
    
    try {
      toast.loading("Generando PDF...");
      const response = await axios.get(`${API}/cases/${caseData.id}/ds160/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DS160_Referencia_${formData.surname || 'Usuario'}_${formData.given_name || ''}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success("PDF descargado exitosamente");
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || "Error al generar el PDF");
    }
  };

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Personal Information 1
        return (
          <div className="space-y-6">
            {/* Passport Scanner Banner */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Scan className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-emerald-900">Escanea tu Pasaporte</h4>
                    <p className="text-sm text-emerald-700">Auto-llena el formulario con IA</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowPassportScanner(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="scan-passport-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Escanear Pasaporte
                </Button>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Apellido(s)" required hint="Como aparece en su pasaporte">
                <Input
                  value={formData.surname || ""}
                  onChange={(e) => updateField("surname", e.target.value)}
                  placeholder="GARCIA LOPEZ"
                  className="uppercase"
                />
              </FormField>
              <FormField label="Nombre(s)" required hint="Como aparece en su pasaporte">
                <Input
                  value={formData.given_name || ""}
                  onChange={(e) => updateField("given_name", e.target.value)}
                  placeholder="MARIA ELENA"
                  className="uppercase"
                />
              </FormField>
            </div>

            <FormField label="Nombre completo en alfabeto nativo" hint="Si su idioma usa otro alfabeto">
              <Input
                value={formData.full_name_native || ""}
                onChange={(e) => updateField("full_name_native", e.target.value)}
                placeholder="Dejar en blanco si usa alfabeto latino"
              />
            </FormField>

            <YesNoField
              label="¿Ha usado otros nombres?"
              hint="Nombres de soltera, apodos, alias, etc."
              value={formData.has_other_names || false}
              onChange={(v) => updateField("has_other_names", v)}
            />
            
            {formData.has_other_names && (
              <FormField label="Otros nombres usados">
                <Input
                  value={formData.other_names || ""}
                  onChange={(e) => updateField("other_names", e.target.value)}
                  placeholder="Ej: GARCIA (nombre de soltera)"
                />
              </FormField>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Sexo" required>
                <Select value={formData.sex || ""} onValueChange={(v) => updateField("sex", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Masculino</SelectItem>
                    <SelectItem value="F">Femenino</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Estado Civil" required>
                <Select value={formData.marital_status || ""} onValueChange={(v) => updateField("marital_status", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Soltero/a</SelectItem>
                    <SelectItem value="married">Casado/a</SelectItem>
                    <SelectItem value="divorced">Divorciado/a</SelectItem>
                    <SelectItem value="widowed">Viudo/a</SelectItem>
                    <SelectItem value="separated">Separado/a</SelectItem>
                    <SelectItem value="common_law">Unión libre</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Fecha de Nacimiento" required>
                <Input
                  type="date"
                  value={formData.birth_date || ""}
                  onChange={(e) => updateField("birth_date", e.target.value)}
                />
              </FormField>
              <FormField label="Ciudad de Nacimiento" required>
                <Input
                  value={formData.birth_city || ""}
                  onChange={(e) => updateField("birth_city", e.target.value)}
                  placeholder="Ciudad donde nació"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Estado/Provincia de Nacimiento">
                <Input
                  value={formData.birth_state || ""}
                  onChange={(e) => updateField("birth_state", e.target.value)}
                  placeholder="Estado o provincia"
                />
              </FormField>
              <FormField label="País de Nacimiento" required>
                <Select value={formData.birth_country || ""} onValueChange={(v) => updateField("birth_country", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar país" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          </div>
        );

      case 2: // Nationality
        return (
          <div className="space-y-6">
            <FormField label="País de Nacionalidad/Ciudadanía" required>
              <Select value={formData.nationality || ""} onValueChange={(v) => updateField("nationality", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar país" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <YesNoField
              label="¿Tiene otra nacionalidad o ciudadanía?"
              value={formData.has_other_nationality || false}
              onChange={(v) => updateField("has_other_nationality", v)}
            />

            {formData.has_other_nationality && (
              <FormField label="Otra nacionalidad">
                <Select value={formData.other_nationality || ""} onValueChange={(v) => updateField("other_nationality", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar país" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            )}

            <YesNoField
              label="¿Es residente permanente de algún país diferente al de su nacionalidad?"
              value={formData.has_permanent_resident || false}
              onChange={(v) => updateField("has_permanent_resident", v)}
            />

            {formData.has_permanent_resident && (
              <FormField label="País de residencia permanente">
                <Select value={formData.permanent_resident_country || ""} onValueChange={(v) => updateField("permanent_resident_country", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar país" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            )}

            <FormField label="Número de Identificación Nacional" hint="CURP, DNI, Cédula, etc.">
              <Input
                value={formData.national_id || ""}
                onChange={(e) => updateField("national_id", e.target.value)}
                placeholder="Número de identificación de su país"
              />
            </FormField>

            <FormField label="Número de Seguro Social de EE.UU." hint="Solo si tiene uno">
              <Input
                value={formData.us_ssn || ""}
                onChange={(e) => updateField("us_ssn", e.target.value)}
                placeholder="XXX-XX-XXXX"
              />
            </FormField>

            <FormField label="Número de Identificación Fiscal de EE.UU." hint="Solo si tiene uno">
              <Input
                value={formData.us_tax_id || ""}
                onChange={(e) => updateField("us_tax_id", e.target.value)}
                placeholder="Número ITIN si aplica"
              />
            </FormField>
          </div>
        );

      case 3: // Address and Contact
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Ingrese su dirección de residencia actual exactamente como aparece en documentos oficiales.
              </p>
            </div>

            <FormField label="Dirección (Calle y Número)" required>
              <Input
                value={formData.home_address || ""}
                onChange={(e) => updateField("home_address", e.target.value)}
                placeholder="Av. Principal #123, Col. Centro"
              />
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Ciudad" required>
                <Input
                  value={formData.home_city || ""}
                  onChange={(e) => updateField("home_city", e.target.value)}
                  placeholder="Ciudad de residencia"
                />
              </FormField>
              <FormField label="Estado/Provincia" required>
                <Input
                  value={formData.home_state || ""}
                  onChange={(e) => updateField("home_state", e.target.value)}
                  placeholder="Estado o provincia"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Código Postal">
                <Input
                  value={formData.home_postal_code || ""}
                  onChange={(e) => updateField("home_postal_code", e.target.value)}
                  placeholder="00000"
                />
              </FormField>
              <FormField label="País" required>
                <Select value={formData.home_country || ""} onValueChange={(v) => updateField("home_country", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar país" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <YesNoField
              label="¿Su dirección de correspondencia es la misma que su domicilio?"
              value={formData.mailing_address_same !== false}
              onChange={(v) => updateField("mailing_address_same", v)}
            />

            {formData.mailing_address_same === false && (
              <FormField label="Dirección de Correspondencia">
                <Textarea
                  value={formData.mailing_address || ""}
                  onChange={(e) => updateField("mailing_address", e.target.value)}
                  placeholder="Dirección completa de correspondencia"
                  rows={3}
                />
              </FormField>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Teléfono Principal" required>
                <Input
                  value={formData.phone_primary || ""}
                  onChange={(e) => updateField("phone_primary", e.target.value)}
                  placeholder="+52 55 1234 5678"
                />
              </FormField>
              <FormField label="Teléfono Secundario">
                <Input
                  value={formData.phone_secondary || ""}
                  onChange={(e) => updateField("phone_secondary", e.target.value)}
                  placeholder="+52 55 8765 4321"
                />
              </FormField>
            </div>

            <FormField label="Teléfono de Trabajo">
              <Input
                value={formData.phone_work || ""}
                onChange={(e) => updateField("phone_work", e.target.value)}
                placeholder="+52 55 0000 0000"
              />
            </FormField>

            <FormField label="Correo Electrónico" required>
              <Input
                type="email"
                value={formData.email || ""}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="su@email.com"
              />
            </FormField>
          </div>
        );

      case 4: // Passport
        return (
          <div className="space-y-6">
            <FormField label="Tipo de Pasaporte" required>
              <Select value={formData.passport_type || ""} onValueChange={(v) => updateField("passport_type", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular/Turista</SelectItem>
                  <SelectItem value="official">Oficial</SelectItem>
                  <SelectItem value="diplomatic">Diplomático</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Número de Pasaporte" required>
                <Input
                  value={formData.passport_number || ""}
                  onChange={(e) => updateField("passport_number", e.target.value.toUpperCase())}
                  placeholder="G12345678"
                  className="uppercase"
                />
              </FormField>
              <FormField label="Número de Libro del Pasaporte" hint="Si aplica">
                <Input
                  value={formData.passport_book_number || ""}
                  onChange={(e) => updateField("passport_book_number", e.target.value)}
                  placeholder="Opcional"
                />
              </FormField>
            </div>

            <FormField label="País que Emitió el Pasaporte" required>
              <Select value={formData.passport_country || ""} onValueChange={(v) => updateField("passport_country", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar país" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Ciudad de Emisión" required>
                <Input
                  value={formData.passport_city_issued || ""}
                  onChange={(e) => updateField("passport_city_issued", e.target.value)}
                  placeholder="Ciudad donde se emitió"
                />
              </FormField>
              <FormField label="Estado/Provincia de Emisión">
                <Input
                  value={formData.passport_state_issued || ""}
                  onChange={(e) => updateField("passport_state_issued", e.target.value)}
                  placeholder="Estado o provincia"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Fecha de Emisión" required>
                <Input
                  type="date"
                  value={formData.passport_issue_date || ""}
                  onChange={(e) => updateField("passport_issue_date", e.target.value)}
                />
              </FormField>
              <FormField label="Fecha de Vencimiento" required>
                <Input
                  type="date"
                  value={formData.passport_expiry_date || ""}
                  onChange={(e) => updateField("passport_expiry_date", e.target.value)}
                />
              </FormField>
            </div>

            <YesNoField
              label="¿Alguna vez ha perdido un pasaporte o le han robado uno?"
              value={formData.has_lost_passport || false}
              onChange={(v) => updateField("has_lost_passport", v)}
            />

            {formData.has_lost_passport && (
              <FormField label="Detalles del pasaporte perdido/robado">
                <Textarea
                  value={formData.lost_passport_details || ""}
                  onChange={(e) => updateField("lost_passport_details", e.target.value)}
                  placeholder="Explique las circunstancias y número de pasaporte si lo recuerda"
                  rows={3}
                />
              </FormField>
            )}
          </div>
        );

      case 5: // Travel
        return (
          <div className="space-y-6">
            <FormField label="Propósito del Viaje" required>
              <Select value={formData.travel_purpose || ""} onValueChange={(v) => updateField("travel_purpose", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar propósito" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tourism">Turismo</SelectItem>
                  <SelectItem value="business">Negocios</SelectItem>
                  <SelectItem value="medical">Tratamiento Médico</SelectItem>
                  <SelectItem value="conference">Conferencia/Convención</SelectItem>
                  <SelectItem value="family_visit">Visita Familiar</SelectItem>
                  <SelectItem value="transit">Tránsito</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            <YesNoField
              label="¿Tiene planes de viaje específicos?"
              hint="Fechas, vuelos y hoteles reservados"
              value={formData.travel_specific_plans || false}
              onChange={(v) => updateField("travel_specific_plans", v)}
            />

            {formData.travel_specific_plans && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Fecha de Llegada">
                    <Input
                      type="date"
                      value={formData.arrival_date || ""}
                      onChange={(e) => updateField("arrival_date", e.target.value)}
                    />
                  </FormField>
                  <FormField label="Ciudad de Llegada">
                    <Input
                      value={formData.arrival_city || ""}
                      onChange={(e) => updateField("arrival_city", e.target.value)}
                      placeholder="Ej: Los Angeles, Miami"
                    />
                  </FormField>
                </div>

                <FormField label="Fecha de Salida">
                  <Input
                    type="date"
                    value={formData.departure_date || ""}
                    onChange={(e) => updateField("departure_date", e.target.value)}
                  />
                </FormField>
              </>
            )}

            <div className="p-4 bg-navy-50 rounded-xl space-y-4">
              <h4 className="font-medium text-navy-900">Dirección donde se hospedará en EE.UU.</h4>
              
              <FormField label="Dirección">
                <Input
                  value={formData.us_address || ""}
                  onChange={(e) => updateField("us_address", e.target.value)}
                  placeholder="123 Main Street"
                />
              </FormField>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="Ciudad">
                  <Input
                    value={formData.us_city || ""}
                    onChange={(e) => updateField("us_city", e.target.value)}
                    placeholder="Ciudad"
                  />
                </FormField>
                <FormField label="Estado">
                  <Select value={formData.us_state || ""} onValueChange={(v) => updateField("us_state", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="ZIP Code">
                  <Input
                    value={formData.us_zip || ""}
                    onChange={(e) => updateField("us_zip", e.target.value)}
                    placeholder="00000"
                  />
                </FormField>
              </div>
            </div>

            <FormField label="¿Quién pagará el viaje?" required>
              <Select value={formData.who_pays_trip || ""} onValueChange={(v) => updateField("who_pays_trip", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Yo mismo</SelectItem>
                  <SelectItem value="employer">Mi empleador</SelectItem>
                  <SelectItem value="family">Familiar</SelectItem>
                  <SelectItem value="other_person">Otra persona</SelectItem>
                  <SelectItem value="other_company">Otra compañía/organización</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            {formData.who_pays_trip && formData.who_pays_trip !== "self" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Nombre de quien paga">
                  <Input
                    value={formData.payer_name || ""}
                    onChange={(e) => updateField("payer_name", e.target.value)}
                    placeholder="Nombre completo o empresa"
                  />
                </FormField>
                <FormField label="Relación">
                  <Input
                    value={formData.payer_relationship || ""}
                    onChange={(e) => updateField("payer_relationship", e.target.value)}
                    placeholder="Ej: Empleador, Padre, etc."
                  />
                </FormField>
              </div>
            )}

            <YesNoField
              label="¿Viajará con otras personas?"
              value={formData.traveling_with_others || false}
              onChange={(v) => updateField("traveling_with_others", v)}
            />

            {formData.traveling_with_others && (
              <FormField label="Nombres de los acompañantes">
                <Textarea
                  value={formData.companion_names || ""}
                  onChange={(e) => updateField("companion_names", e.target.value)}
                  placeholder="Nombre y relación de cada acompañante"
                  rows={3}
                />
              </FormField>
            )}
          </div>
        );

      case 6: // Previous US Travel
        return (
          <div className="space-y-6">
            <YesNoField
              label="¿Ha viajado a Estados Unidos anteriormente?"
              value={formData.has_previous_us_travel || false}
              onChange={(v) => updateField("has_previous_us_travel", v)}
            />

            {formData.has_previous_us_travel && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Fecha de última llegada">
                    <Input
                      type="date"
                      value={formData.previous_arrival_date || ""}
                      onChange={(e) => updateField("previous_arrival_date", e.target.value)}
                    />
                  </FormField>
                  <FormField label="Duración de la estadía">
                    <Input
                      value={formData.previous_stay_length || ""}
                      onChange={(e) => updateField("previous_stay_length", e.target.value)}
                      placeholder="Ej: 2 semanas, 3 meses"
                    />
                  </FormField>
                </div>

                <YesNoField
                  label="¿Tiene o ha tenido licencia de conducir de EE.UU.?"
                  value={formData.has_us_drivers_license || false}
                  onChange={(v) => updateField("has_us_drivers_license", v)}
                />

                {formData.has_us_drivers_license && (
                  <FormField label="Estado de la licencia">
                    <Select value={formData.us_drivers_license_state || ""} onValueChange={(v) => updateField("us_drivers_license_state", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar estado" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                )}
              </>
            )}

            <YesNoField
              label="¿Ha tenido visa de EE.UU. anteriormente?"
              value={formData.has_previous_visa || false}
              onChange={(v) => updateField("has_previous_visa", v)}
            />

            {formData.has_previous_visa && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Número de visa anterior">
                    <Input
                      value={formData.previous_visa_number || ""}
                      onChange={(e) => updateField("previous_visa_number", e.target.value)}
                      placeholder="Número de la visa"
                    />
                  </FormField>
                  <FormField label="Fecha de emisión">
                    <Input
                      type="date"
                      value={formData.previous_visa_issue_date || ""}
                      onChange={(e) => updateField("previous_visa_issue_date", e.target.value)}
                    />
                  </FormField>
                </div>

                <YesNoField
                  label="¿Está solicitando el mismo tipo de visa?"
                  value={formData.same_visa_type !== false}
                  onChange={(v) => updateField("same_visa_type", v)}
                />
              </>
            )}

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <h4 className="font-medium text-amber-800 mb-4">Preguntas Importantes</h4>
              
              <div className="space-y-4">
                <YesNoField
                  label="¿Alguna vez le han negado una visa de EE.UU.?"
                  value={formData.visa_was_refused || false}
                  onChange={(v) => updateField("visa_was_refused", v)}
                />

                {formData.visa_was_refused && (
                  <FormField label="Detalles de la negación">
                    <Textarea
                      value={formData.visa_refused_details || ""}
                      onChange={(e) => updateField("visa_refused_details", e.target.value)}
                      placeholder="Explique cuándo, dónde y por qué fue negada"
                      rows={3}
                    />
                  </FormField>
                )}

                <YesNoField
                  label="¿Alguna vez le han cancelado o revocado una visa de EE.UU.?"
                  value={formData.visa_was_cancelled || false}
                  onChange={(v) => updateField("visa_was_cancelled", v)}
                />

                {formData.visa_was_cancelled && (
                  <FormField label="Detalles de la cancelación">
                    <Textarea
                      value={formData.visa_cancelled_details || ""}
                      onChange={(e) => updateField("visa_cancelled_details", e.target.value)}
                      placeholder="Explique las circunstancias"
                      rows={3}
                    />
                  </FormField>
                )}

                <YesNoField
                  label="¿Alguien ha presentado una petición de inmigración a su nombre?"
                  value={formData.has_petition_filed || false}
                  onChange={(v) => updateField("has_petition_filed", v)}
                />

                {formData.has_petition_filed && (
                  <FormField label="Detalles de la petición">
                    <Textarea
                      value={formData.petition_details || ""}
                      onChange={(e) => updateField("petition_details", e.target.value)}
                      placeholder="Tipo de petición, quién la presentó, estado actual"
                      rows={3}
                    />
                  </FormField>
                )}
              </div>
            </div>
          </div>
        );

      case 7: // US Contact
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-800">
                Ingrese la información de una persona u organización de contacto en Estados Unidos. Puede ser un hotel, familiar, empresa o persona que visitará.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Nombre del Contacto/Organización" required>
                <Input
                  value={formData.us_contact_name || ""}
                  onChange={(e) => updateField("us_contact_name", e.target.value)}
                  placeholder="Nombre o nombre del hotel/empresa"
                />
              </FormField>
              <FormField label="Relación">
                <Select value={formData.us_contact_relationship || ""} onValueChange={(v) => updateField("us_contact_relationship", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="family">Familiar</SelectItem>
                    <SelectItem value="friend">Amigo</SelectItem>
                    <SelectItem value="business">Contacto de negocios</SelectItem>
                    <SelectItem value="employer">Empleador</SelectItem>
                    <SelectItem value="school">Escuela/Universidad</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <FormField label="Organización (si aplica)">
              <Input
                value={formData.us_contact_organization || ""}
                onChange={(e) => updateField("us_contact_organization", e.target.value)}
                placeholder="Nombre de la empresa u organización"
              />
            </FormField>

            <FormField label="Dirección del Contacto" required>
              <Input
                value={formData.us_contact_address || ""}
                onChange={(e) => updateField("us_contact_address", e.target.value)}
                placeholder="Dirección completa"
              />
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField label="Ciudad" required>
                <Input
                  value={formData.us_contact_city || ""}
                  onChange={(e) => updateField("us_contact_city", e.target.value)}
                  placeholder="Ciudad"
                />
              </FormField>
              <FormField label="Estado" required>
                <Select value={formData.us_contact_state || ""} onValueChange={(v) => updateField("us_contact_state", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="ZIP Code">
                <Input
                  value={formData.us_contact_zip || ""}
                  onChange={(e) => updateField("us_contact_zip", e.target.value)}
                  placeholder="00000"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Teléfono del Contacto">
                <Input
                  value={formData.us_contact_phone || ""}
                  onChange={(e) => updateField("us_contact_phone", e.target.value)}
                  placeholder="+1 000 000 0000"
                />
              </FormField>
              <FormField label="Email del Contacto">
                <Input
                  type="email"
                  value={formData.us_contact_email || ""}
                  onChange={(e) => updateField("us_contact_email", e.target.value)}
                  placeholder="contacto@email.com"
                />
              </FormField>
            </div>
          </div>
        );

      case 8: // Family
        return (
          <div className="space-y-6">
            <div className="p-4 bg-navy-50 rounded-xl space-y-4">
              <h4 className="font-medium text-navy-900">Información del Padre</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Apellido(s) del Padre">
                  <Input
                    value={formData.father_surname || ""}
                    onChange={(e) => updateField("father_surname", e.target.value)}
                    placeholder="Apellidos"
                  />
                </FormField>
                <FormField label="Nombre(s) del Padre">
                  <Input
                    value={formData.father_given_name || ""}
                    onChange={(e) => updateField("father_given_name", e.target.value)}
                    placeholder="Nombres"
                  />
                </FormField>
              </div>

              <FormField label="Fecha de Nacimiento del Padre">
                <Input
                  type="date"
                  value={formData.father_birth_date || ""}
                  onChange={(e) => updateField("father_birth_date", e.target.value)}
                />
              </FormField>

              <YesNoField
                label="¿Su padre está en EE.UU.?"
                value={formData.father_in_us || false}
                onChange={(v) => updateField("father_in_us", v)}
              />

              {formData.father_in_us && (
                <FormField label="Estatus migratorio del padre">
                  <Select value={formData.father_us_status || ""} onValueChange={(v) => updateField("father_us_status", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="citizen">Ciudadano</SelectItem>
                      <SelectItem value="permanent_resident">Residente Permanente</SelectItem>
                      <SelectItem value="nonimmigrant">No Inmigrante</SelectItem>
                      <SelectItem value="other">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              )}
            </div>

            <div className="p-4 bg-navy-50 rounded-xl space-y-4">
              <h4 className="font-medium text-navy-900">Información de la Madre</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Apellido(s) de la Madre">
                  <Input
                    value={formData.mother_surname || ""}
                    onChange={(e) => updateField("mother_surname", e.target.value)}
                    placeholder="Apellidos"
                  />
                </FormField>
                <FormField label="Nombre(s) de la Madre">
                  <Input
                    value={formData.mother_given_name || ""}
                    onChange={(e) => updateField("mother_given_name", e.target.value)}
                    placeholder="Nombres"
                  />
                </FormField>
              </div>

              <FormField label="Fecha de Nacimiento de la Madre">
                <Input
                  type="date"
                  value={formData.mother_birth_date || ""}
                  onChange={(e) => updateField("mother_birth_date", e.target.value)}
                />
              </FormField>

              <YesNoField
                label="¿Su madre está en EE.UU.?"
                value={formData.mother_in_us || false}
                onChange={(v) => updateField("mother_in_us", v)}
              />

              {formData.mother_in_us && (
                <FormField label="Estatus migratorio de la madre">
                  <Select value={formData.mother_us_status || ""} onValueChange={(v) => updateField("mother_us_status", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="citizen">Ciudadana</SelectItem>
                      <SelectItem value="permanent_resident">Residente Permanente</SelectItem>
                      <SelectItem value="nonimmigrant">No Inmigrante</SelectItem>
                      <SelectItem value="other">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              )}
            </div>

            <YesNoField
              label="¿Tiene parientes inmediatos en EE.UU. (además de padres)?"
              hint="Hermanos, hijos, cónyuge"
              value={formData.has_immediate_relatives_us || false}
              onChange={(v) => updateField("has_immediate_relatives_us", v)}
            />

            {formData.has_immediate_relatives_us && (
              <FormField label="Información de parientes en EE.UU.">
                <Textarea
                  value={formData.relatives_in_us || ""}
                  onChange={(e) => updateField("relatives_in_us", e.target.value)}
                  placeholder="Nombre, relación y estatus migratorio de cada pariente"
                  rows={4}
                />
              </FormField>
            )}

            {(formData.marital_status === "married" || formData.marital_status === "common_law") && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-4">
                <h4 className="font-medium text-emerald-900">Información del Cónyuge</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Apellido(s) del Cónyuge">
                    <Input
                      value={formData.spouse_surname || ""}
                      onChange={(e) => updateField("spouse_surname", e.target.value)}
                      placeholder="Apellidos"
                    />
                  </FormField>
                  <FormField label="Nombre(s) del Cónyuge">
                    <Input
                      value={formData.spouse_given_name || ""}
                      onChange={(e) => updateField("spouse_given_name", e.target.value)}
                      placeholder="Nombres"
                    />
                  </FormField>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Fecha de Nacimiento">
                    <Input
                      type="date"
                      value={formData.spouse_birth_date || ""}
                      onChange={(e) => updateField("spouse_birth_date", e.target.value)}
                    />
                  </FormField>
                  <FormField label="Nacionalidad">
                    <Select value={formData.spouse_nationality || ""} onValueChange={(v) => updateField("spouse_nationality", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="País" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>

                <FormField label="Ciudad de Nacimiento del Cónyuge">
                  <Input
                    value={formData.spouse_birth_city || ""}
                    onChange={(e) => updateField("spouse_birth_city", e.target.value)}
                    placeholder="Ciudad"
                  />
                </FormField>

                <FormField label="Dirección actual del Cónyuge">
                  <Input
                    value={formData.spouse_address || ""}
                    onChange={(e) => updateField("spouse_address", e.target.value)}
                    placeholder="Dirección completa (si es diferente a la suya)"
                  />
                </FormField>
              </div>
            )}
          </div>
        );

      case 9: // Work
        return (
          <div className="space-y-6">
            <FormField label="Ocupación Principal" required>
              <Select value={formData.occupation || ""} onValueChange={(v) => updateField("occupation", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar ocupación" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employed">Empleado</SelectItem>
                  <SelectItem value="self_employed">Trabajador Independiente</SelectItem>
                  <SelectItem value="business_owner">Dueño de Negocio</SelectItem>
                  <SelectItem value="student">Estudiante</SelectItem>
                  <SelectItem value="retired">Jubilado</SelectItem>
                  <SelectItem value="homemaker">Ama de Casa</SelectItem>
                  <SelectItem value="unemployed">Desempleado</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            {(formData.occupation === "employed" || formData.occupation === "self_employed" || formData.occupation === "business_owner") && (
              <>
                <FormField label="Nombre del Empleador/Empresa" required>
                  <Input
                    value={formData.employer_name || ""}
                    onChange={(e) => updateField("employer_name", e.target.value)}
                    placeholder="Nombre de la empresa"
                  />
                </FormField>

                <FormField label="Dirección del Trabajo">
                  <Input
                    value={formData.employer_address || ""}
                    onChange={(e) => updateField("employer_address", e.target.value)}
                    placeholder="Dirección de la oficina/negocio"
                  />
                </FormField>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField label="Ciudad">
                    <Input
                      value={formData.employer_city || ""}
                      onChange={(e) => updateField("employer_city", e.target.value)}
                      placeholder="Ciudad"
                    />
                  </FormField>
                  <FormField label="Estado/Provincia">
                    <Input
                      value={formData.employer_state || ""}
                      onChange={(e) => updateField("employer_state", e.target.value)}
                      placeholder="Estado"
                    />
                  </FormField>
                  <FormField label="País">
                    <Select value={formData.employer_country || ""} onValueChange={(v) => updateField("employer_country", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="País" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Teléfono del Trabajo">
                    <Input
                      value={formData.employer_phone || ""}
                      onChange={(e) => updateField("employer_phone", e.target.value)}
                      placeholder="+52 55 0000 0000"
                    />
                  </FormField>
                  <FormField label="Fecha de Inicio">
                    <Input
                      type="date"
                      value={formData.employment_start_date || ""}
                      onChange={(e) => updateField("employment_start_date", e.target.value)}
                    />
                  </FormField>
                </div>

                <FormField label="Ingreso Mensual (USD)" hint="Aproximado en dólares">
                  <Input
                    value={formData.monthly_income || ""}
                    onChange={(e) => updateField("monthly_income", e.target.value)}
                    placeholder="Ej: 2500"
                  />
                </FormField>

                <FormField label="Descripción de sus funciones">
                  <Textarea
                    value={formData.job_duties || ""}
                    onChange={(e) => updateField("job_duties", e.target.value)}
                    placeholder="Describa brevemente sus responsabilidades laborales"
                    rows={3}
                  />
                </FormField>
              </>
            )}

            <YesNoField
              label="¿Ha tenido empleos anteriores?"
              value={formData.previously_employed || false}
              onChange={(v) => updateField("previously_employed", v)}
            />

            {formData.previously_employed && (
              <FormField label="Empleador(es) Anterior(es)">
                <Textarea
                  value={formData.previous_employer || ""}
                  onChange={(e) => updateField("previous_employer", e.target.value)}
                  placeholder="Nombre de la empresa, fechas de empleo y puesto"
                  rows={3}
                />
              </FormField>
            )}
          </div>
        );

      case 10: // Education
        return (
          <div className="space-y-6">
            <FormField label="Nivel Educativo Más Alto" required>
              <Select value={formData.education_level || ""} onValueChange={(v) => updateField("education_level", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar nivel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin educación formal</SelectItem>
                  <SelectItem value="primary">Primaria</SelectItem>
                  <SelectItem value="secondary">Secundaria</SelectItem>
                  <SelectItem value="high_school">Preparatoria/Bachillerato</SelectItem>
                  <SelectItem value="technical">Técnico/Vocacional</SelectItem>
                  <SelectItem value="university">Universidad (sin título)</SelectItem>
                  <SelectItem value="bachelors">Licenciatura</SelectItem>
                  <SelectItem value="masters">Maestría</SelectItem>
                  <SelectItem value="doctorate">Doctorado</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            {formData.education_level && formData.education_level !== "none" && (
              <>
                <FormField label="Nombre de la Institución">
                  <Input
                    value={formData.school_name || ""}
                    onChange={(e) => updateField("school_name", e.target.value)}
                    placeholder="Nombre de la escuela/universidad"
                  />
                </FormField>

                <FormField label="Dirección de la Institución">
                  <Input
                    value={formData.school_address || ""}
                    onChange={(e) => updateField("school_address", e.target.value)}
                    placeholder="Dirección"
                  />
                </FormField>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Ciudad">
                    <Input
                      value={formData.school_city || ""}
                      onChange={(e) => updateField("school_city", e.target.value)}
                      placeholder="Ciudad"
                    />
                  </FormField>
                  <FormField label="País">
                    <Select value={formData.school_country || ""} onValueChange={(v) => updateField("school_country", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="País" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>

                <FormField label="Carrera/Especialidad">
                  <Input
                    value={formData.school_course || ""}
                    onChange={(e) => updateField("school_course", e.target.value)}
                    placeholder="Ej: Administración de Empresas"
                  />
                </FormField>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Fecha de Inicio">
                    <Input
                      type="date"
                      value={formData.school_start_date || ""}
                      onChange={(e) => updateField("school_start_date", e.target.value)}
                    />
                  </FormField>
                  <FormField label="Fecha de Término">
                    <Input
                      type="date"
                      value={formData.school_end_date || ""}
                      onChange={(e) => updateField("school_end_date", e.target.value)}
                    />
                  </FormField>
                </div>
              </>
            )}

            <FormField label="Idiomas que habla" hint="Además del español">
              <Input
                value={formData.languages || ""}
                onChange={(e) => updateField("languages", e.target.value)}
                placeholder="Ej: Inglés (intermedio), Francés (básico)"
              />
            </FormField>

            <FormField label="Países visitados en los últimos 5 años">
              <Textarea
                value={formData.countries_visited_5_years || ""}
                onChange={(e) => updateField("countries_visited_5_years", e.target.value)}
                placeholder="Liste los países visitados (turismo, negocios, etc.)"
                rows={3}
              />
            </FormField>

            <FormField label="Redes Sociales" hint="Facebook, Instagram, Twitter, LinkedIn, etc.">
              <Textarea
                value={formData.social_media_platforms || ""}
                onChange={(e) => updateField("social_media_platforms", e.target.value)}
                placeholder="Plataforma: nombre de usuario (Ej: Facebook: juan.garcia)"
                rows={3}
              />
            </FormField>
          </div>
        );

      case 11: // Security
        return (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-red-800">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                <strong>Importante:</strong> Responda honestamente todas las preguntas. Mentir en esta sección puede resultar en prohibición permanente de entrada a EE.UU.
              </p>
            </div>

            <div className="space-y-4">
              <YesNoField
                label="¿Tiene alguna enfermedad transmisible de importancia para la salud pública?"
                value={formData.security_disease || false}
                onChange={(v) => updateField("security_disease", v)}
              />

              <YesNoField
                label="¿Tiene algún trastorno mental o físico que pueda representar una amenaza?"
                value={formData.security_disorder || false}
                onChange={(v) => updateField("security_disorder", v)}
              />

              <YesNoField
                label="¿Es o ha sido adicto a las drogas?"
                value={formData.security_drug_user || false}
                onChange={(v) => updateField("security_drug_user", v)}
              />

              <YesNoField
                label="¿Alguna vez ha sido arrestado o condenado por algún delito?"
                value={formData.security_arrested || false}
                onChange={(v) => updateField("security_arrested", v)}
              />

              <YesNoField
                label="¿Ha violado alguna ley relacionada con sustancias controladas?"
                value={formData.security_controlled_substances || false}
                onChange={(v) => updateField("security_controlled_substances", v)}
              />

              <YesNoField
                label="¿Ha participado en prostitución o trata de personas?"
                value={formData.security_prostitution || false}
                onChange={(v) => updateField("security_prostitution", v)}
              />

              <YesNoField
                label="¿Ha participado en lavado de dinero?"
                value={formData.security_money_laundering || false}
                onChange={(v) => updateField("security_money_laundering", v)}
              />

              <YesNoField
                label="¿Ha participado o tiene intención de participar en actividades terroristas?"
                value={formData.security_terrorism || false}
                onChange={(v) => updateField("security_terrorism", v)}
              />

              <YesNoField
                label="¿Ha sido miembro de una organización terrorista?"
                value={formData.security_terrorist_org || false}
                onChange={(v) => updateField("security_terrorist_org", v)}
              />

              <YesNoField
                label="¿Ha participado en genocidio, tortura o ejecuciones extrajudiciales?"
                value={formData.security_genocide || false}
                onChange={(v) => updateField("security_genocide", v)}
              />

              <YesNoField
                label="¿Ha sido deportado de EE.UU. o se ha quedado más tiempo del permitido?"
                value={formData.security_deportation || false}
                onChange={(v) => updateField("security_deportation", v)}
              />

              <YesNoField
                label="¿Ha retenido la custodia de un menor ciudadano estadounidense?"
                value={formData.security_child_custody || false}
                onChange={(v) => updateField("security_child_custody", v)}
              />

              <YesNoField
                label="¿Ha votado ilegalmente en EE.UU.?"
                value={formData.security_voting_violation || false}
                onChange={(v) => updateField("security_voting_violation", v)}
              />

              <YesNoField
                label="¿Ha evadido impuestos en EE.UU.?"
                value={formData.security_tax_evasion || false}
                onChange={(v) => updateField("security_tax_evasion", v)}
              />
            </div>
          </div>
        );

      case 12: // Confirmation
        return (
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">
                ¡Casi Terminado!
              </h3>
              <p className="text-navy-600">
                Has completado todas las secciones del asistente DS-160.
              </p>
            </div>

            <div className="bg-white border border-navy-200 rounded-xl p-6">
              <h4 className="font-medium text-navy-900 mb-4">Resumen de Información</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-navy-500">Nombre Completo</p>
                  <p className="font-medium text-navy-900">{formData.surname} {formData.given_name}</p>
                </div>
                <div>
                  <p className="text-navy-500">Fecha de Nacimiento</p>
                  <p className="font-medium text-navy-900">{formData.birth_date || "No especificado"}</p>
                </div>
                <div>
                  <p className="text-navy-500">Pasaporte</p>
                  <p className="font-medium text-navy-900">{formData.passport_number || "No especificado"}</p>
                </div>
                <div>
                  <p className="text-navy-500">Propósito del Viaje</p>
                  <p className="font-medium text-navy-900">{formData.travel_purpose || "No especificado"}</p>
                </div>
              </div>
            </div>

            <FormField label="Número de Confirmación DS-160" hint="Una vez que envíe su solicitud en ceac.state.gov">
              <Input
                value={formData.confirmation_number || ""}
                onChange={(e) => updateField("confirmation_number", e.target.value.toUpperCase())}
                placeholder="AA00XXXXXX"
                className="uppercase text-lg font-mono"
              />
            </FormField>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-medium text-amber-800 mb-2">Próximos Pasos:</h4>
              <ol className="list-decimal list-inside text-sm text-amber-700 space-y-1">
                <li>Visite <strong>ceac.state.gov</strong> y complete el formulario DS-160 oficial</li>
                <li>Use esta información como guía para llenar el formulario</li>
                <li>Guarde su número de confirmación (empieza con AA)</li>
                <li>Ingrese el número de confirmación arriba</li>
              </ol>
            </div>

            <YesNoField
              label="¿Ha completado y enviado su DS-160 en ceac.state.gov?"
              value={formData.completed || false}
              onChange={(v) => updateField("completed", v)}
            />
          </div>
        );

      default:
        return <p>Paso no encontrado</p>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const currentStepInfo = STEPS.find(s => s.id === currentStep);
  const CurrentIcon = currentStepInfo?.icon || FileText;

  return (
    <DashboardLayout>
      <div className="flex gap-8">
        {/* Step Progress Sidebar */}
        <StepProgress 
          currentStep={currentStep} 
          onStepClick={goToStep}
          completedSteps={completedSteps}
          savedData={savedData}
        />

        {/* Main Content */}
        <div className="flex-1 max-w-3xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/dashboard")}
                className="text-navy-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleExportPDF}
                disabled={!caseData?.id}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid="export-ds160-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowHelp(true)}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Ayuda IA
              </Button>
            </div>
          </div>

          {/* Overall Progress Summary */}
          <ProgressSummary 
            savedData={savedData} 
            onStepClick={goToStep} 
            saving={saving}
            hasUnsavedChanges={hasUnsavedChanges}
          />

          {/* Oficial Comments Alert */}
          {oficialComments.length > 0 && (
            <OficialCommentsAlert 
              comments={oficialComments}
              pendingCount={pendingCorrections}
              onViewComment={(fieldId) => {
                // Find which step this field belongs to
                const stepId = Object.entries(STEP_FIELDS).find(([step, fields]) => 
                  fields.includes(fieldId)
                )?.[0];
                if (stepId) {
                  goToStep(parseInt(stepId));
                  toast.info(`Navegando al campo: ${FIELD_HELP[fieldId]?.title || fieldId}`);
                }
              }}
            />
          )}

          {/* Risk Alerts - use formData for real-time detection */}
          <RiskAlerts formData={formData} />

          {/* Document Checklist - use formData for real-time updates */}
          <DocumentChecklist 
            formData={formData} 
            uploadedDocs={[]} 
            onUpload={(docId) => {
              toast.info("Redirigiendo a la sección de documentos...");
              navigate("/documents");
            }}
          />

          {/* Step Header */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-navy-100 shadow-sm overflow-hidden mb-6"
          >
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <CurrentIcon className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <p className="text-emerald-100 text-sm">Paso {currentStep} de {STEPS.length}</p>
                  <h1 className="font-heading text-2xl font-bold">{currentStepInfo?.name}</h1>
                  <p className="text-emerald-100">{currentStepInfo?.description}</p>
                </div>
                {/* Current Step Progress - shows saved data only */}
                {(() => {
                  const progress = calculateStepProgress(currentStep, savedData);
                  if (progress.total === 0) return null;
                  return (
                    <div className="text-right">
                      <div className="text-2xl font-bold">{progress.filled}/{progress.total}</div>
                      <div className="text-emerald-100 text-sm">
                        {saving ? "guardando..." : "guardadas"}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="h-2 bg-navy-100">
              <div 
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>
          </motion.div>

          {/* Step Content */}
          <motion.div
            key={`content-${currentStep}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-navy-100 p-6 shadow-sm mb-6"
          >
            {renderStepContent()}
          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={goBack}
              disabled={currentStep === 1}
              className="text-navy-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => handleSave(false)}
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>

              <Button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : currentStep === STEPS.length ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Finalizar
                  </>
                ) : (
                  <>
                    Siguiente
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* AI Help Modal */}
      <AIHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        currentStep={currentStep}
      />

      {/* Passport Scanner Modal */}
      {showPassportScanner && (
        <PassportScanner
          onDataExtracted={handlePassportData}
          onClose={() => setShowPassportScanner(false)}
        />
      )}
    </DashboardLayout>
  );
};

export default DS160Assistant;
